import { useEffect, useRef } from 'react';
import { Map, FullscreenControl, GeolocateControl, NavigationControl, ScaleControl, GlobeControl } from 'maplibre-gl';
import useGeoData from '../../../store/useGeoData';

interface UseMapControlsProps {
    map: Map | null;
    isMapLoaded: boolean;
}

export const useMapControls = ({ map, isMapLoaded }: UseMapControlsProps) => { // Rimosso isTerrainSourceReady dalle props
    // Refs per tenere traccia delle istanze dei controlli
    const fullscreenControlRef = useRef<FullscreenControl | null>(null);
    const geolocateControlRef = useRef<GeolocateControl | null>(null);
    const navigationControlRef = useRef<NavigationControl | null>(null);
    const scaleControlRef = useRef<ScaleControl | null>(null);
    const globeControlRef = useRef<GlobeControl | null>(null);

    // Leggi gli stati di attivazione dei controlli dallo store
    const isFullscreenControlActive = useGeoData(state => state.isFullscreenControlActive);
    const isGeolocateControlActive = useGeoData(state => state.isGeolocateControlActive);
    const isNavigationControlActive = useGeoData(state => state.isNavigationControlActive);
    const isScaleControlActive = useGeoData(state => state.isScaleControlActive);
    const isGlobeControlActive = useGeoData(state => state.isGlobeControlActive);

    // Funzione helper per aggiungere un controllo
    const addControl = (controlInstance: any, position: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left', controlRef: React.MutableRefObject<any | null>) => {
        if (!map || controlRef.current) return; // Non aggiungere se mappa non pronta o controllo già esistente
        try {
            map.addControl(controlInstance, position);
            controlRef.current = controlInstance;
        } catch (error) {
            controlRef.current = null; // Resetta se l'aggiunta fallisce
        }
    };

    // Funzione helper per rimuovere un controllo
    const removeControl = (controlRef: React.MutableRefObject<any | null>) => {
        if (!map || !controlRef.current) return; // Non rimuovere se mappa non pronta o controllo non esistente
        try {
            map.removeControl(controlRef.current);
        } catch (error) {
            // Silenzia errori se la mappa o il controllo sono già stati rimossi
            if (error instanceof Error && !error.message.includes('Map was removed') && !error.message.includes('Control does not exist')) {
            }
        } finally {
             controlRef.current = null; // Imposta sempre a null dopo il tentativo di rimozione
        }
    };

    // Effect per FullscreenControl
    useEffect(() => {
        if (!isMapLoaded || !map) return; // Richiede mappa caricata
        if (isFullscreenControlActive) {
            addControl(new FullscreenControl(), 'top-right', fullscreenControlRef);
        } else {
            removeControl(fullscreenControlRef);
        }
    }, [isFullscreenControlActive, isMapLoaded, map]);

    // Effect per GeolocateControl
    useEffect(() => {
        if (!isMapLoaded || !map) return; // Richiede mappa caricata
        if (isGeolocateControlActive) {
            addControl(new GeolocateControl({ positionOptions: { enableHighAccuracy: true }, trackUserLocation: true, showAccuracyCircle: true }), 'top-right', geolocateControlRef);
        } else {
            removeControl(geolocateControlRef);
        }
    }, [isGeolocateControlActive, isMapLoaded, map]);

    // Effect per NavigationControl
    useEffect(() => {
        if (!isMapLoaded || !map) return; // Richiede mappa caricata
        if (isNavigationControlActive) {
            addControl(new NavigationControl({ visualizePitch: true, visualizeRoll: true }), 'top-right', navigationControlRef);
        } else {
            removeControl(navigationControlRef);
        }
    }, [isNavigationControlActive, isMapLoaded, map]);

    // Effect per ScaleControl
    useEffect(() => {
        if (!isMapLoaded || !map) return; // Richiede mappa caricata
        if (isScaleControlActive) {
            addControl(new ScaleControl({ maxWidth: 150, unit: 'metric' }), 'bottom-left', scaleControlRef);
        } else {
            removeControl(scaleControlRef);
        }
    }, [isScaleControlActive, isMapLoaded, map]);

    // Effect per GlobeControl
    useEffect(() => {
        if (!isMapLoaded || !map) return; // Richiede mappa caricata
        if (isGlobeControlActive) {
            addControl(new GlobeControl(), 'top-right', globeControlRef); // Posizione corretta
        } else {
            removeControl(globeControlRef);
        }
        // Cleanup specifico per GlobeControl (anche se già gestito da removeControl)
        return () => {
            if (map && globeControlRef.current) {
                removeControl(globeControlRef);
            }
        };
    }, [isGlobeControlActive, isMapLoaded, map]);

    // Cleanup generale: rimuove tutti i controlli gestiti quando l'hook viene smontato o la mappa cambia
    useEffect(() => {
        return () => {
            removeControl(fullscreenControlRef);
            removeControl(geolocateControlRef);
            removeControl(navigationControlRef);
            // removeControl(terrainControlRef); // Rimosso cleanup
            removeControl(scaleControlRef);
            removeControl(globeControlRef);
        };
    }, [map]); // Dipende solo dalla mappa
};